import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InstallPrompt } from "@/components/InstallPrompt";
import { 
  Heart, Feather, Wind, Sparkles, Moon, Sun, 
  MessageCircle, LogOut, RefreshCw, Book, Headphones, Calendar, Crown, ExternalLink, Volume2, Settings, Award, Shield, Lock, ChevronDown, ChevronUp, Check, Circle, Brain, Music, Clock, Flame, Send, Leaf, TrendingUp, TreeDeciduous, Phone, Users, Star, X, Gift, Copy, Compass, LayoutGrid, PenLine
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { SelfGarden } from "@/components/SelfGarden";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [quizResult, setQuizResult] = useState(null);
  const [dailyContent, setDailyContent] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({});
  const [playlists, setPlaylists] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Loving Kindness state
  const [lovingKindness, setLovingKindness] = useState(null);
  const [kindnessStreak, setKindnessStreak] = useState(null);
  const [kindnessInput, setKindnessInput] = useState("");
  const [submittingKindness, setSubmittingKindness] = useState(false);
  
  // Spirit AI quick chat state
  const [spiritQuestion, setSpiritQuestion] = useState("");
  const [spiritResponse, setSpiritResponse] = useState(null);
  const [spiritSessionId, setSpiritSessionId] = useState(null);
  const [spiritMessages, setSpiritMessages] = useState([]);
  const [askingSpirit, setAskingSpirit] = useState(false);
  
  // Expandable content states
  const [expandedMantra, setExpandedMantra] = useState(false);
  const [expandedBlessing, setExpandedBlessing] = useState(false);
  const [expandedAffirmation, setExpandedAffirmation] = useState(false);
  
  // Mood Tracker state
  const [moodData, setMoodData] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [submittingMood, setSubmittingMood] = useState(false);
  
  // Dandelion mood emojis
  const DANDELION_MOODS = [
    { level: 1, emoji: "🥀", label: "Very Low" },
    { level: 2, emoji: "🌱", label: "Low" },
    { level: 3, emoji: "🌼", label: "Neutral" },
    { level: 4, emoji: "🌻", label: "Good" },
    { level: 5, emoji: "🌾", label: "Vibrant" }
  ];

  
  // Review prompt state
  const [showReviewCard, setShowReviewCard] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [reviewDismissed, setReviewDismissed] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, title: "", content: "", display_name: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Referral state
  const [referralCode, setReferralCode] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);

  // Weekly progress state
  const [weeklyProgress, setWeeklyProgress] = useState(null);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);

  useEffect(() => {
    fetchData();
    checkReviewEligibility();
    fetchReferralCode();
    fetchWeeklyProgress();
    checkOnboarding();
    requestNotificationPermission();
  }, []);

  const checkReviewEligibility = async () => {
    try {
      const permanentlyDismissed = localStorage.getItem('review_permanently_dismissed');
      if (permanentlyDismissed === 'true') {
        setReviewDismissed(true);
        return;
      }
      
      const alreadyReviewed = localStorage.getItem('user_has_reviewed');
      if (alreadyReviewed === 'true') {
        setHasReviewed(true);
        return;
      }
      
      const res = await axios.get(`${API}/reviews/can-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.can_review) {
        setCanReview(true);
        setShowReviewCard(true);
      }
    } catch (error) {
      console.error("Review eligibility check failed:", error);
    }
  };
  
  const dismissReviewCard = (permanent = false) => {
    setShowReviewCard(false);
    if (permanent) {
      localStorage.setItem('review_permanently_dismissed', 'true');
      setReviewDismissed(true);
    }
  };

  const fetchReferralCode = async () => {
    try {
      const res = await axios.get(`${API}/referrals/code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralCode(res.data.code);
    } catch (error) {
      console.error("Failed to fetch referral code:", error);
    }
  };

  const fetchWeeklyProgress = async () => {
    try {
      const res = await axios.get(`${API}/progress/weekly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyProgress(res.data);
    } catch (error) {
      console.error("Failed to fetch weekly progress:", error);
    }
  };

  const checkOnboarding = () => {
    const onboardingDone = localStorage.getItem('onboarding_completed');
    if (!onboardingDone) {
      setShowOnboarding(true);
    }
  };

  const completeOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('onboarding_completed', 'true');
  };

  const requestNotificationPermission = () => {
    if (localStorage.getItem('notifications_enabled') === 'false') return;
    if ('Notification' in window && Notification.permission === 'default') {
      const hasAsked = localStorage.getItem('notification_asked');
      if (!hasAsked) {
        setTimeout(() => {
          Notification.requestPermission().then(permission => {
            localStorage.setItem('notification_asked', 'true');
            if (permission === 'granted') {
              localStorage.setItem('notifications_enabled', 'true');
              scheduleReminder();
            }
          });
        }, 10000);
      }
    } else if ('Notification' in window && Notification.permission === 'granted') {
      scheduleReminder();
    }
  };

  const scheduleReminder = () => {
    if (localStorage.getItem('notifications_enabled') === 'false') return;
    const now = new Date();
    const evening = new Date();
    evening.setHours(20, 0, 0, 0);
    if (now < evening) {
      const delay = evening.getTime() - now.getTime();
      setTimeout(() => {
        if (document.hidden) {
          new Notification("Authentic Self 365", {
            body: "Your evening blessing is waiting. Take a moment to check in with yourself.",
            icon: "/favicon.ico"
          });
        }
      }, delay);
    }
  };

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setReferralCopied(true);
      toast.success("Referral code copied! Share it with friends 💜");
      setTimeout(() => setReferralCopied(false), 3000);
    }
  };

  const submitReview = async () => {
    if (!reviewData.title.trim() || !reviewData.content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews/submit`, reviewData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Thank you for sharing your experience! 💜");
      setShowReviewForm(false);
      setShowReviewCard(false);
      setHasReviewed(true);
      setCanReview(false);
      localStorage.setItem('user_has_reviewed', 'true');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const fetchData = async () => {
    try {
      const [quizRes, dailyRes, subRes, progressRes, playlistsRes, kindnessRes, moodRes] = await Promise.all([
        axios.get(`${API}/quiz/result`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/content/daily`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/progress/today`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/content/playlists`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/loving-kindness/today`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mood/today`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setQuizResult(quizRes.data);
      setDailyContent(dailyRes.data);
      setSubscription(subRes.data);
      setProgress(progressRes.data.completed || {});
      setPlaylists(playlistsRes.data.playlists || []);
      setIsPremium(playlistsRes.data.is_premium || false);
      setLovingKindness(kindnessRes.data.declaration);
      setKindnessStreak(kindnessRes.data.streak);
      if (kindnessRes.data.declaration) {
        setKindnessInput(kindnessRes.data.declaration.declaration);
      }
      
      setMoodData(moodRes.data);
      if (moodRes.data.entry) {
        setSelectedMood(moodRes.data.entry.mood_level);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsDone = async (contentType, contentId, e) => {
    e?.stopPropagation();
    try {
      const res = await axios.post(`${API}/progress/mark-done`, 
        { content_type: contentType, content_id: contentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const key = `${contentType}:${contentId}`;
      if (res.data.status === 'marked') {
        setProgress(prev => ({ ...prev, [key]: true }));
        
        if (contentType === 'meditation' || contentType === 'breathing') {
          try {
            const activityType = contentType === 'breathing' ? 'breathwork' : 'meditation';
            await axios.post(`${API}/garden/grow?activity_type=${activityType}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Complete! Your garden is growing 🌱");
          } catch (gardenErr) {
            toast.success("Marked as complete!");
          }
        } else {
          toast.success("Marked as complete!");
        }
      } else {
        setProgress(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
        toast.success("Unmarked");
      }
    } catch (error) {
      console.error("Error marking progress:", error);
      toast.error("Failed to update progress");
    }
  };

  const isComplete = (contentType, contentId) => {
    return progress[`${contentType}:${contentId}`] === true;
  };

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const formatTrialTime = (hours) => {
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.round(hours % 24);
      return `${days} day${days > 1 ? 's' : ''} ${remainingHours > 0 ? `${remainingHours}h` : ''}`;
    }
    return `${Math.round(hours)} hours`;
  };

  const handleSubmitKindness = async () => {
    if (!kindnessInput.trim()) {
      toast.error("Please share your act of loving kindness");
      return;
    }
    
    setSubmittingKindness(true);
    try {
      const response = await axios.post(
        `${API}/loving-kindness/declare`,
        { declaration: kindnessInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLovingKindness(response.data);
      
      const streakRes = await axios.get(
        `${API}/loving-kindness/streak`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKindnessStreak(streakRes.data);
      
      toast.success("💜 Your declaration of loving kindness has been recorded!");
    } catch (error) {
      toast.error("Unable to save your declaration. Please try again.");
    } finally {
      setSubmittingKindness(false);
    }
  };

  const handleMoodSelect = async (level) => {
    setSelectedMood(level);
    setSubmittingMood(true);
    
    try {
      const response = await axios.post(
        `${API}/mood/log`,
        { mood_level: level },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const moodRes = await axios.get(
        `${API}/mood/today`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMoodData(moodRes.data);
      
      const moodLabel = DANDELION_MOODS.find(m => m.level === level)?.label || "";
      toast.success(`${DANDELION_MOODS.find(m => m.level === level)?.emoji} Feeling ${moodLabel} today - recorded!`);
    } catch (error) {
      toast.error("Unable to log your mood. Please try again.");
      setSelectedMood(moodData?.entry?.mood_level || null);
    } finally {
      setSubmittingMood(false);
    }
  };

  const handleAskSpirit = async () => {
    if (!spiritQuestion.trim()) {
      toast.error("Please enter a question for Spirit");
      return;
    }
    
    setAskingSpirit(true);
    setSpiritResponse(null);
    
    const userMessage = { role: "user", content: spiritQuestion };
    const updatedMessages = [...spiritMessages, userMessage];
    setSpiritMessages(updatedMessages);
    
    try {
      const response = await axios.post(
        `${API}/spirit/chat`,
        { 
          message: spiritQuestion,
          session_id: spiritSessionId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newSessionId = response.data.session_id;
      setSpiritSessionId(newSessionId);
      
      const spiritMessage = { role: "spirit", content: response.data.response };
      const finalMessages = [...updatedMessages, spiritMessage];
      setSpiritMessages(finalMessages);
      setSpiritResponse(response.data.response);
      
      localStorage.setItem('spiritChatSession', JSON.stringify({
        sessionId: newSessionId,
        messages: finalMessages
      }));
      
    } catch (error) {
      setSpiritMessages(spiritMessages);
      if (error.response?.status === 403) {
        toast.error("Premium subscription required for Spirit Chat");
      } else {
        toast.error("Unable to connect with Spirit. Please try again.");
      }
    } finally {
      setAskingSpirit(false);
    }
  };
  
  const handleOpenFullChat = () => {
    navigate("/spirit");
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const practiceModules = [
    { testid: "daily-rhythm-card", route: "/rhythm", icon: Clock, color: "text-emerald-700", bg: "from-emerald-500/15 to-teal-500/10", title: "Daily Rhythm", sub: "My Day · Me · Us" },
    { testid: "ei-card", route: "/emotional-intelligence", icon: Brain, color: "text-accent", bg: "from-accent/15 to-primary/10", title: "EI Modules", sub: "22 Lessons" },
    { testid: "healthy-habits-card", route: "/healthy-habits", icon: Leaf, color: "text-green-600", bg: "from-green-500/15 to-emerald-500/10", title: "Healthy Habits", sub: "20 Modules" },
    { testid: "shadow-self-card", route: "/shadow-self", icon: Moon, color: "text-teal-600", bg: "from-teal-500/15 to-emerald-500/10", title: "Shadow Self", sub: "22 Sessions" },
    { testid: "energy-mechanics-card", route: "/energy-mechanics", icon: Flame, color: "text-rose-600", bg: "from-rose-500/15 to-orange-500/10", title: "Energy Mechanics", sub: "15 Lessons" },
    { testid: "ancestral-wisdom-card", route: "/ancestral-wisdom", icon: TreeDeciduous, color: "text-amber-600", bg: "from-amber-500/15 to-orange-500/10", title: "Ancestral Wisdom", sub: "12 Sessions" },
    { testid: "guided-practices-card", route: "/guided-practices", icon: Headphones, color: "text-primary", bg: "from-primary/15 to-accent/10", title: "Guided Audio", sub: "TTS Meditations" },
    { testid: "meditations-card", route: "/meditations", icon: Moon, color: "text-primary", bg: "from-primary/15 to-teal-500/10", title: "Meditations", sub: "52 practices" },
    { testid: "breathing-card", route: "/breathing", icon: Wind, color: "text-primary", bg: "from-primary/10 to-cyan-500/10", title: "Breathing", sub: "30 exercises" },
    { testid: "favorites-card", route: "/meditations?favorites=true", icon: Heart, color: "text-red-500", bg: "from-amber-400/15 to-orange-400/10", title: "My Favorites", sub: "Quick Access" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center" data-testid="dashboard-loading">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Heart className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="dashboard-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/60">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <div className="leading-tight">
              <span className="font-heading text-xl font-semibold text-foreground block">Authentic Self 365</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-primary font-bold hidden sm:block">Dandelion Farm · House of Bloom</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://intentionalinsights.gumroad.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 bg-gradient-to-r from-amber-50 to-teal-50 px-4 py-2 rounded-full border border-amber-200 transition-colors"
              data-testid="gumroad-store-cta"
            >
              <Sparkles className="w-4 h-4" />
              JVDH Intentional Insights
              <ExternalLink className="w-3 h-3" />
            </a>
            {subscription?.is_founder && (
              <button 
                onClick={() => navigate("/architects")}
                className="flex items-center gap-1 text-sm text-amber-600 bg-gradient-to-r from-amber-100 to-amber-50 px-3 py-1 rounded-full border border-amber-200 hover:shadow-md transition-all founder-badge-glow"
                title={`Founding Member #${subscription.user_number} - Click to access Architects Portal`}
                data-testid="founder-badge"
              >
                <Award className="w-4 h-4" />
                <span className="hidden sm:inline">Founder #{subscription.user_number}</span>
                <span className="sm:hidden">#{subscription.user_number}</span>
              </button>
            )}
            {!subscription?.is_premium && (
              <Button
                onClick={() => navigate("/subscription")}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-4 py-2 text-sm font-medium"
                data-testid="go-premium-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Go Premium
              </Button>
            )}
            {subscription?.is_premium && (
              <span className="flex items-center gap-1 text-sm text-primary bg-primary/10 px-3 py-1 rounded-full">
                <Crown className="w-4 h-4" />
                Premium
              </span>
            )}
            {user?.email === "julie@jvdh.au" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                className="text-primary hover:text-primary/80"
                data-testid="admin-btn"
                title="Admin Dashboard"
              >
                <Shield className="w-5 h-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings")}
              className="text-muted-foreground hover:text-foreground"
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Onboarding Flow - 3 steps for new users */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="glass-card rounded-3xl border-0 max-w-md mx-4 overflow-hidden animate-in fade-in slide-in-from-bottom-4">
            <CardContent className="p-8 text-center">
              {onboardingStep === 0 && (
                <>
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500/20 to-amber-500/20 flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-10 h-10 text-teal-600" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-3">Welcome Home</h2>
                  <p className="text-muted-foreground mb-6">
                    This is your digital sanctuary. Let's get you settled in with 3 simple steps.
                  </p>
                  <div className="flex justify-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                    <div className="w-3 h-3 rounded-full bg-muted"></div>
                    <div className="w-3 h-3 rounded-full bg-muted"></div>
                  </div>
                  <Button onClick={() => setOnboardingStep(1)} className="btn-primary w-full" data-testid="onboarding-next-1">
                    Let's Begin
                  </Button>
                </>
              )}
              {onboardingStep === 1 && (
                <>
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-10 h-10 text-teal-600" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-3">Take the Quiz</h2>
                  <p className="text-muted-foreground mb-6">
                    Discover your current State of Being. It takes 3 minutes and unlocks personalised content just for you.
                  </p>
                  <div className="flex justify-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                    <div className="w-3 h-3 rounded-full bg-muted"></div>
                  </div>
                  <Button onClick={() => setOnboardingStep(2)} className="btn-primary w-full" data-testid="onboarding-next-2">
                    Next
                  </Button>
                </>
              )}
              {onboardingStep === 2 && (
                <>
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500/20 to-teal-500/20 flex items-center justify-center mx-auto mb-6">
                    <Moon className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-3">Start Small</h2>
                  <p className="text-muted-foreground mb-6">
                    Pick one tool — a meditation, a journal prompt, or just log your mood. That's all you need today. Your garden will grow from here.
                  </p>
                  <div className="flex justify-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                  </div>
                  <Button onClick={completeOnboarding} className="btn-primary w-full" data-testid="onboarding-complete">
                    I'm Ready
                  </Button>
                </>
              )}
              <button onClick={completeOnboarding} className="text-xs text-muted-foreground mt-4 hover:text-foreground">
                Skip
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="glass-card rounded-3xl border-0 max-w-lg mx-4 overflow-hidden animate-in fade-in slide-in-from-bottom-4">
            <CardContent className="p-8">
              <button 
                onClick={() => setShowReviewForm(false)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/30 flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                  Share Your Experience
                </h2>
                <p className="text-muted-foreground text-sm">
                  Your words could be the reason someone starts their own journey home to themselves.
                </p>
              </div>
              
              <div className="flex justify-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewData(prev => ({ ...prev, rating: star }))}
                    className="transition-transform hover:scale-110"
                    data-testid={`review-star-${star}`}
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewData.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Title</label>
                  <Input
                    placeholder="Sum up your experience in a few words..."
                    value={reviewData.title}
                    onChange={(e) => setReviewData(prev => ({ ...prev, title: e.target.value }))}
                    className="rounded-xl"
                    data-testid="review-title-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Your Experience</label>
                  <Textarea
                    placeholder="What has this journey meant to you? How has it helped?"
                    value={reviewData.content}
                    onChange={(e) => setReviewData(prev => ({ ...prev, content: e.target.value }))}
                    className="rounded-xl min-h-[100px]"
                    data-testid="review-content-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Display Name (Optional)</label>
                  <Input
                    placeholder="How would you like to be known?"
                    value={reviewData.display_name}
                    onChange={(e) => setReviewData(prev => ({ ...prev, display_name: e.target.value }))}
                    className="rounded-xl"
                    data-testid="review-name-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowReviewForm(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitReview}
                  disabled={submittingReview}
                  className="flex-1 btn-primary rounded-xl"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? "Submitting..." : "Share"}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center mt-4">
                Reviews appear on the site after approval. Thank you! 💜
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <InstallPrompt />

        {/* Gentle Review Request Card */}
        {showReviewCard && canReview && !hasReviewed && !reviewDismissed && (
          <Card className="glass-card rounded-2xl border-0 mb-6 border-l-4 border-l-amber-400" data-testid="review-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-rose-500/10 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-rose-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Your experience matters to us
                    </p>
                    <p className="text-xs text-muted-foreground">
                      If this journey has touched you, would you share a few words? It helps others find their way here too.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => setShowReviewForm(true)}
                    className="bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-xs"
                    data-testid="write-review-btn"
                  >
                    Write a Review
                  </Button>
                  <button
                    onClick={() => dismissReviewCard(true)}
                    className="text-muted-foreground hover:text-foreground p-1"
                    title="Don't show again"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trial Banner */}
        {subscription?.is_trial && subscription?.trial_hours_remaining && (
          <Card className="glass-card rounded-2xl border-0 mb-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10" data-testid="trial-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">
                      ⏰ Trial: <span className="text-amber-600">{formatTrialTime(subscription.trial_hours_remaining)}</span> remaining
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Enjoying full premium access
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/subscription")}
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                >
                  Manage Trial
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Greeting header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <span className="overline-label">Day {dailyContent?.day_of_year} of 365</span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-foreground mt-1 tracking-tight">
              {getTimeGreeting()}, {user?.name?.split(" ")[0]}
            </h1>
          </div>
          <p className="text-muted-foreground text-sm hidden md:block">
            One small practice today keeps your garden growing.
          </p>
        </div>

        {/* Tabbed layout */}
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="dashboard-tabs">
          <TabsList className="w-full sm:w-auto flex sm:inline-flex bg-white/80 border border-stone-200/80 rounded-full p-1.5 h-auto mb-8 shadow-sm overflow-x-auto">
            <TabsTrigger value="overview" className="rounded-full px-5 py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-white flex-1 sm:flex-none" data-testid="tab-overview">
              <LayoutGrid className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="my-path" className="rounded-full px-5 py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-white flex-1 sm:flex-none" data-testid="tab-my-path">
              <Compass className="w-4 h-4 mr-2" />
              My Path
            </TabsTrigger>
            <TabsTrigger value="journal" className="rounded-full px-5 py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-white flex-1 sm:flex-none" data-testid="tab-journal">
              <PenLine className="w-4 h-4 mr-2" />
              Journal
            </TabsTrigger>
            <TabsTrigger value="community" className="rounded-full px-5 py-2.5 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-white flex-1 sm:flex-none" data-testid="tab-community">
              <Users className="w-4 h-4 mr-2" />
              Community
            </TabsTrigger>
          </TabsList>

          {/* ============ OVERVIEW TAB - Bento Grid ============ */}
          <TabsContent value="overview" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
              {/* Spirit AI tile (large) */}
              {subscription?.is_premium ? (
                <Card className="bento-card border-primary/15 md:col-span-4 bg-gradient-to-br from-white to-teal-50/60" data-testid="spirit-ai-card">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="font-heading text-2xl font-semibold text-foreground">
                            Ask Spirit
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Quick guidance from your AI companion
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleOpenFullChat}
                        className="text-primary hover:text-primary/80"
                      >
                        Full Chat →
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <Input
                          placeholder="What's on your heart today?"
                          value={spiritQuestion}
                          onChange={(e) => setSpiritQuestion(e.target.value)}
                          className="flex-1 rounded-full bg-white border-stone-200 focus:border-primary px-5"
                          disabled={askingSpirit}
                          onKeyDown={(e) => e.key === 'Enter' && !askingSpirit && handleAskSpirit()}
                          data-testid="spirit-input"
                        />
                        <Button
                          onClick={handleAskSpirit}
                          disabled={askingSpirit || !spiritQuestion.trim()}
                          className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
                          data-testid="spirit-submit-btn"
                        >
                          {askingSpirit ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      
                      {spiritResponse && (
                        <div className="bg-white/70 rounded-2xl p-4 border border-teal-100">
                          <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                            {spiritResponse}
                          </p>
                        </div>
                      )}
                      
                      {!spiritResponse && (
                        <p className="text-sm text-muted-foreground italic font-heading text-center">
                          "I am here to listen, guide, and remind you of the wisdom you already hold within."
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card 
                  className="bento-card md:col-span-4 bg-gradient-to-br from-white to-teal-50/50 cursor-pointer"
                  onClick={() => navigate("/subscription")}
                  data-testid="spirit-ai-cta-card"
                >
                  <CardContent className="p-0 h-full flex items-center">
                    <div className="flex items-center gap-5 w-full">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Lock className="w-6 h-6 text-primary/70" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-2xl font-semibold text-foreground mb-1">
                          Unlock Spirit AI
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Get unlimited guidance from your personal AI companion with Premium
                        </p>
                      </div>
                      <Crown className="w-6 h-6 text-accent" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily Affirmation */}
              <Card 
                className="bento-card md:col-span-2 cursor-pointer" 
                data-testid="affirmation-card"
                onClick={() => setExpandedAffirmation(!expandedAffirmation)}
              >
                <CardContent className="p-0 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <span className="overline-label">Today's Affirmation</span>
                    {expandedAffirmation ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <p className="font-heading text-xl text-foreground italic leading-relaxed flex-1" data-testid="daily-affirmation">
                    "{dailyContent?.affirmation?.text}"
                  </p>
                  {expandedAffirmation && dailyContent?.affirmation?.category && (
                    <div className="mt-4 pt-4 border-t border-border/60">
                      <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                        {dailyContent.affirmation.category}
                      </span>
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate("/affirmations"); }}
                    className="mt-4 text-primary hover:text-primary/80 text-sm font-medium text-left"
                    data-testid="view-all-affirmations-btn"
                  >
                    View all affirmations →
                  </button>
                </CardContent>
              </Card>

              {/* Self Garden */}
              <div className="md:col-span-3">
                <SelfGarden />
              </div>

              {/* Mood Tracker */}
              <Card className="bento-card md:col-span-3" data-testid="mood-tracker-card">
                <CardContent className="p-0">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <span className="overline-label">Daily Check-in</span>
                      <h3 className="font-heading text-2xl font-semibold text-foreground mt-0.5">
                        How are you feeling?
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {moodData?.streak > 0 && (
                        <div className="flex items-center gap-1 bg-orange-100 px-3 py-1.5 rounded-full">
                          <Flame className="w-4 h-4 text-orange-500" />
                          <span className="font-semibold text-orange-600 text-sm">
                            {moodData.streak}d
                          </span>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/mood-progress")}
                        className="text-accent hover:text-accent/80"
                      >
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Progress
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between gap-2 mb-4">
                    {DANDELION_MOODS.map((mood) => (
                      <button
                        key={mood.level}
                        onClick={() => handleMoodSelect(mood.level)}
                        disabled={submittingMood}
                        className={`flex-1 flex flex-col items-center p-3 rounded-2xl transition-all duration-200 ${
                          selectedMood === mood.level
                            ? 'bg-amber-100 ring-2 ring-amber-400 scale-105'
                            : 'bg-stone-50 hover:bg-amber-50'
                        } ${submittingMood ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                        data-testid={`mood-btn-${mood.level}`}
                      >
                        <span className="text-3xl mb-1">{mood.emoji}</span>
                        <span className={`text-xs font-medium ${
                          selectedMood === mood.level ? 'text-amber-700' : 'text-muted-foreground'
                        }`}>
                          {mood.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                  {moodData?.logged_today && (
                    <div className="flex items-center justify-center gap-2 text-sm text-green-600">
                      <Check className="w-4 h-4" />
                      <span>Today's mood logged</span>
                    </div>
                  )}
                  
                  {moodData?.badges && moodData.badges.length > 0 && (
                    <div className="flex items-center justify-center gap-2 mt-3">
                      {moodData.badges.slice(0, 2).map((badge) => (
                        <span 
                          key={badge}
                          className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1"
                        >
                          <Award className="w-3 h-3" />
                          {badge}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Today's Meditation quick-start */}
              <Card 
                className={`bento-card ${weeklyProgress && weeklyProgress.total_activities > 0 ? 'md:col-span-2' : 'md:col-span-3'} ${isComplete('meditation', dailyContent?.meditation?.id) ? 'ring-2 ring-green-500/40' : ''}`}
                data-testid="daily-meditation-card"
              >
                <CardContent className="p-0">
                  <div className="flex items-start justify-between mb-3">
                    <span className="overline-label">Today's Meditation</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => markAsDone('meditation', dailyContent?.meditation?.id, e)}
                      className={`rounded-full h-8 w-8 flex-shrink-0 ${isComplete('meditation', dailyContent?.meditation?.id) ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-muted hover:bg-muted/80'}`}
                      data-testid="mark-meditation-done"
                    >
                      {isComplete('meditation', dailyContent?.meditation?.id) ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Circle className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <div className="cursor-pointer" onClick={() => navigate("/meditations")}>
                    <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center mb-3">
                      <Moon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-foreground leading-snug">
                      {dailyContent?.meditation?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {dailyContent?.meditation?.duration} • {dailyContent?.meditation?.focus}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Today's Breathwork quick-start */}
              <Card 
                className={`bento-card ${weeklyProgress && weeklyProgress.total_activities > 0 ? 'md:col-span-2' : 'md:col-span-3'} ${isComplete('breathing', dailyContent?.breathing?.id) ? 'ring-2 ring-green-500/40' : ''}`}
                data-testid="daily-breathing-card"
              >
                <CardContent className="p-0">
                  <div className="flex items-start justify-between mb-3">
                    <span className="overline-label text-accent">Today's Breathwork</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => markAsDone('breathing', dailyContent?.breathing?.id, e)}
                      className={`rounded-full h-8 w-8 flex-shrink-0 ${isComplete('breathing', dailyContent?.breathing?.id) ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-muted hover:bg-muted/80'}`}
                      data-testid="mark-breathing-done"
                    >
                      {isComplete('breathing', dailyContent?.breathing?.id) ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Circle className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <div className="cursor-pointer" onClick={() => navigate("/breathing")}>
                    <div className="w-12 h-12 rounded-2xl bg-accent/15 flex items-center justify-center mb-3">
                      <Wind className="w-6 h-6 text-accent" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-foreground leading-snug">
                      {dailyContent?.breathing?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {dailyContent?.breathing?.cycles} cycles • {dailyContent?.breathing?.inhale_duration}-{dailyContent?.breathing?.hold_duration}-{dailyContent?.breathing?.exhale_duration}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Progress */}
              {weeklyProgress && weeklyProgress.total_activities > 0 && (
                <Card className="bento-card md:col-span-2 bg-gradient-to-br from-white to-teal-50/50" data-testid="weekly-progress-card">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="overline-label">Your Week</span>
                        <p className="text-xs text-muted-foreground mt-0.5">{weeklyProgress.active_days}/7 days active</p>
                      </div>
                      <div className="text-right">
                        <p className="font-heading text-3xl font-bold text-primary">{weeklyProgress.total_activities}</p>
                        <p className="text-xs text-muted-foreground">activities</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {weeklyProgress.meditations > 0 && (
                        <div className="text-center p-2 rounded-xl bg-stone-50">
                          <p className="text-lg font-bold text-foreground">{weeklyProgress.meditations}</p>
                          <p className="text-[10px] text-muted-foreground">Meditations</p>
                        </div>
                      )}
                      {weeklyProgress.breathwork > 0 && (
                        <div className="text-center p-2 rounded-xl bg-stone-50">
                          <p className="text-lg font-bold text-foreground">{weeklyProgress.breathwork}</p>
                          <p className="text-[10px] text-muted-foreground">Breathwork</p>
                        </div>
                      )}
                      {weeklyProgress.journal_entries > 0 && (
                        <div className="text-center p-2 rounded-xl bg-stone-50">
                          <p className="text-lg font-bold text-foreground">{weeklyProgress.journal_entries}</p>
                          <p className="text-[10px] text-muted-foreground">Journal</p>
                        </div>
                      )}
                      {weeklyProgress.mood_logs > 0 && (
                        <div className="text-center p-2 rounded-xl bg-stone-50">
                          <p className="text-lg font-bold text-foreground">{weeklyProgress.mood_logs}</p>
                          <p className="text-[10px] text-muted-foreground">Mood Logs</p>
                        </div>
                      )}
                      {weeklyProgress.kindness > 0 && (
                        <div className="text-center p-2 rounded-xl bg-stone-50">
                          <p className="text-lg font-bold text-foreground">{weeklyProgress.kindness}</p>
                          <p className="text-[10px] text-muted-foreground">Kindness</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Loving Kindness */}
              <Card className="bento-card md:col-span-6 bg-gradient-to-br from-white to-amber-50/60" data-testid="loving-kindness-card">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row md:items-center gap-5">
                    <div className="flex items-center gap-3 md:w-1/3">
                      <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Heart className="w-6 h-6 text-amber-500" strokeWidth={1.5} fill="currentColor" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-semibold text-foreground leading-snug">
                          Daily Loving Kindness
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Training your mind to see the good
                        </p>
                      </div>
                      {kindnessStreak && kindnessStreak.current_streak > 0 && (
                        <div className="flex items-center gap-1 bg-orange-100 px-2.5 py-1 rounded-full ml-auto md:ml-0">
                          <Flame className="w-4 h-4 text-orange-500" />
                          <span className="font-semibold text-orange-600 text-sm">
                            {kindnessStreak.current_streak}d
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex gap-3">
                        <Input
                          placeholder="I showed kindness today by..."
                          value={kindnessInput}
                          onChange={(e) => setKindnessInput(e.target.value)}
                          className="flex-1 rounded-full bg-white border-amber-200 focus:border-amber-400 px-5"
                          disabled={submittingKindness}
                          data-testid="kindness-input"
                        />
                        <Button
                          onClick={handleSubmitKindness}
                          disabled={submittingKindness || !kindnessInput.trim()}
                          className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6"
                          data-testid="kindness-submit-btn"
                        >
                          {submittingKindness ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : lovingKindness ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      {lovingKindness && (
                        <div className="flex items-center gap-2 text-sm text-green-600 mt-2">
                          <Check className="w-4 h-4" />
                          <span>Today's declaration recorded</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============ MY PATH TAB ============ */}
          <TabsContent value="my-path" className="mt-0 space-y-6">
            {/* Daily Rhythm featured banner */}
            <Card
              className="rounded-3xl border-0 overflow-hidden cursor-pointer group text-white hover:shadow-[0_8px_30px_rgb(74,107,93,0.35)] transition-shadow"
              style={{ background: "linear-gradient(120deg, #4A6B5D, #0F766E)" }}
              onClick={() => navigate("/rhythm")}
              data-testid="rhythm-featured-banner"
            >
              <CardContent className="p-6 md:p-7 flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Clock className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading text-xl md:text-2xl font-semibold">Daily Rhythm</h2>
                  <p className="text-sm opacity-85 mt-0.5">Your day at a glance — timeline, check-ins, streaks & home rhythm</p>
                </div>
                <span className="text-xs font-bold bg-white/20 px-4 py-2 rounded-full flex-shrink-0 group-hover:bg-white group-hover:text-teal-800 transition-colors">
                  Open →
                </span>
              </CardContent>
            </Card>

            {/* State of Being Quiz Discovery */}
            <Card 
              className="rounded-3xl border border-stone-200/80 overflow-hidden bg-gradient-to-r from-teal-50/80 via-white to-amber-50/60 hover:shadow-[0_8px_30px_rgb(28,36,33,0.08)] transition-shadow cursor-pointer group"
              onClick={() => navigate("/quiz")}
              data-testid="quiz-discovery-card"
            >
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500/25 to-amber-500/25 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Sparkles className="w-10 h-10 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
                        State of Being Quiz
                      </h2>
                      {quizResult && (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          <Check className="w-3 h-3" /> Completed
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-4 max-w-xl">
                      {quizResult 
                        ? `Your current state: "${quizResult.state}". Retake anytime to reflect on your evolving journey.`
                        : "Discover your current state of being with our 9-question reflective quiz. Your answers unlock personalised mantras, blessings, and guidance."
                      }
                    </p>
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                      <Button 
                        className="btn-primary group-hover:shadow-lg transition-shadow"
                        onClick={(e) => { e.stopPropagation(); navigate("/quiz"); }}
                      >
                        {quizResult ? "Retake Quiz" : "Begin Your Discovery"}
                        <RefreshCw className={`w-4 h-4 ml-2 ${quizResult ? '' : 'hidden'}`} />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        ✨ 9 reflective questions • 3-5 minutes
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Current State + Mantra/Blessing */}
            {quizResult && (
              <Card className="bento-card" data-testid="state-card">
                <CardContent className="p-0">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <span className="overline-label">Your Current State</span>
                      <h2 className="font-heading text-3xl font-semibold text-foreground mt-1">
                        {quizResult.state}
                      </h2>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate("/quiz")}
                      className="text-muted-foreground hover:text-foreground"
                      data-testid="retake-quiz-btn"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <p className="text-muted-foreground leading-relaxed">
                    {quizResult.description}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div 
                      className="bg-stone-50 rounded-2xl p-5 cursor-pointer transition-colors hover:bg-stone-100"
                      onClick={() => setExpandedMantra(!expandedMantra)}
                      data-testid="mantra-box"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sun className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">Morning Mantra</span>
                        </div>
                        {expandedMantra ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <p className="font-heading text-xl text-foreground italic leading-relaxed" data-testid="dashboard-morning-mantra">
                        "{expandedMantra ? quizResult.morning_mantra : quizResult.morning_mantra?.substring(0, 100) + '...'}"
                      </p>
                      {!expandedMantra && (
                        <p className="text-xs text-primary mt-2">Tap to expand</p>
                      )}
                    </div>
                    
                    <div 
                      className="bg-amber-50/70 rounded-2xl p-5 cursor-pointer transition-colors hover:bg-amber-50"
                      onClick={() => setExpandedBlessing(!expandedBlessing)}
                      data-testid="blessing-box"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Moon className="w-4 h-4 text-accent" />
                          <span className="text-sm font-medium">Evening Blessing</span>
                        </div>
                        {expandedBlessing ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <p className="font-heading text-xl text-foreground italic leading-relaxed" data-testid="dashboard-evening-blessing">
                        "{expandedBlessing ? quizResult.evening_blessing : quizResult.evening_blessing?.substring(0, 100) + '...'}"
                      </p>
                      {!expandedBlessing && (
                        <p className="text-xs text-accent mt-2">Tap to expand</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Your Practices */}
            <div>
              <div className="flex items-end justify-between mb-5">
                <div>
                  <span className="overline-label">Grow at your own pace</span>
                  <h2 className="font-heading text-3xl font-semibold text-foreground mt-0.5">
                    Your Practices
                  </h2>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {practiceModules.map((mod) => (
                  <Card 
                    key={mod.testid}
                    className="bg-white rounded-3xl border border-stone-200/80 cursor-pointer card-hover"
                    onClick={() => navigate(mod.route)}
                    data-testid={mod.testid}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${mod.bg} flex items-center justify-center mx-auto mb-4`}>
                        <mod.icon className={`w-7 h-7 ${mod.color} ${mod.testid === 'favorites-card' ? 'fill-red-500' : ''}`} strokeWidth={1.5} />
                      </div>
                      <h3 className="font-medium text-foreground mb-1">{mod.title}</h3>
                      <p className={`text-xs font-medium ${mod.color}`}>{mod.sub}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Pattern Insights - Premium */}
            {subscription?.is_premium && (
              <Card 
                className="bento-card cursor-pointer hover:scale-[1.005] transition-transform bg-gradient-to-r from-white to-teal-50/50"
                onClick={() => navigate("/insights")}
                data-testid="pattern-insights-card"
              >
                <CardContent className="p-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Brain className="w-6 h-6 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-heading text-2xl font-semibold text-foreground">
                          Pattern Insights
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          AI-powered analysis of your growth journey
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-primary">
                      <TrendingUp className="w-5 h-5" />
                      <span className="text-sm font-medium">Discover →</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Today's Recommendations */}
            <div>
              <div className="mb-5">
                <span className="overline-label">Curated for today</span>
                <h2 className="font-heading text-3xl font-semibold text-foreground mt-0.5">
                  Recommendations
                </h2>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-white rounded-3xl border border-stone-200/80 card-hover" data-testid="daily-reading-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Book className="w-6 h-6 text-primary" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <span className="overline-label text-[10px]">Recommended Reading</span>
                        <h3 className="font-heading text-lg font-semibold text-foreground mt-1">
                          {dailyContent?.reading?.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          by {dailyContent?.reading?.author}
                        </p>
                        <span className="inline-block mt-2 text-xs bg-muted px-2 py-1 rounded-full">
                          {dailyContent?.reading?.category}
                        </span>
                        {dailyContent?.reading?.url && (
                          <a
                            href={dailyContent.reading.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                            aria-label={`Find ${dailyContent.reading.title} by ${dailyContent.reading.author} on Booktopia (opens in new tab)`}
                            data-testid="reading-external-link"
                          >
                            <span>Find this book</span>
                            <ExternalLink className="w-4 h-4" aria-hidden="true" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white rounded-3xl border border-stone-200/80 card-hover" data-testid="daily-listening-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                        <Headphones className="w-6 h-6 text-accent" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <span className="overline-label text-[10px] text-accent">Recommended Listening</span>
                        <h3 className="font-heading text-lg font-semibold text-foreground mt-1">
                          {dailyContent?.listening?.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {dailyContent?.listening?.type}
                        </p>
                        <span className="inline-block mt-2 text-xs bg-muted px-2 py-1 rounded-full">
                          {dailyContent?.listening?.category}
                        </span>
                        {dailyContent?.listening?.url && (
                          <a
                            href={dailyContent.listening.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 flex items-center gap-2 text-sm text-accent hover:text-accent/80 font-medium transition-colors"
                            aria-label={`Listen to ${dailyContent.listening.title} (opens in new tab)`}
                            data-testid="listening-external-link"
                          >
                            <span>Listen now</span>
                            <ExternalLink className="w-4 h-4" aria-hidden="true" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white rounded-3xl border border-stone-200/80 card-hover" data-testid="authentic-self-playlist">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Music className="w-6 h-6 text-primary" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <span className="overline-label text-[10px]">Featured Playlist</span>
                        <h3 className="font-heading text-lg font-semibold text-foreground mt-1">
                          Authentic Self 365
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Curated music for your self-discovery journey
                        </p>
                        <a
                          href="https://open.spotify.com/playlist/4IhzlUjIhDVWtHHwv14X6X"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                        >
                          <span>Open on Spotify</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ============ JOURNAL TAB ============ */}
          <TabsContent value="journal" className="mt-0 space-y-6">
            <Card 
              className="rounded-3xl border border-stone-200/80 overflow-hidden bg-gradient-to-br from-white via-amber-50/40 to-white cursor-pointer group hover:shadow-[0_8px_30px_rgb(28,36,33,0.08)] transition-shadow"
              onClick={() => navigate("/journal")}
              data-testid="journal-card"
            >
              <CardContent className="p-8 md:p-12">
                <div className="max-w-2xl">
                  <span className="overline-label">Your private pages</span>
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2 mb-4">
                    Today I...
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    A blank page waiting for whatever needs to come out — a brain dump, a celebration, a worry set down. Plus 365 guided prompts to walk you deeper, one day at a time.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <Button className="btn-primary" data-testid="open-journal-btn">
                      <Feather className="w-4 h-4 mr-2" />
                      Open Your Journal
                    </Button>
                    <span className="text-sm text-muted-foreground">Every entry grows your garden 🌱</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-5">
              <Card className="bento-card cursor-pointer card-hover" onClick={() => navigate("/journal")} data-testid="journal-prompts-card">
                <CardContent className="p-0">
                  <div className="w-12 h-12 rounded-2xl bg-accent/15 flex items-center justify-center mb-4">
                    <Feather className="w-6 h-6 text-accent" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">365 Guided Prompts</h3>
                  <p className="text-sm text-muted-foreground">
                    A year of thoughtful questions to explore your inner world. Today's prompt is ready for you.
                  </p>
                </CardContent>
              </Card>

              <Card className="bento-card bg-gradient-to-br from-white to-teal-50/40">
                <CardContent className="p-0 flex flex-col justify-center h-full">
                  <p className="font-heading text-xl md:text-2xl text-foreground italic leading-relaxed mb-3">
                    "You are not lost, dear one. You are simply finding your way back to the truth of who you've always been."
                  </p>
                  <span className="font-handwritten text-lg text-primary">— Spirit</span>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============ COMMUNITY TAB ============ */}
          <TabsContent value="community" className="mt-0 space-y-6">
            <div className="grid md:grid-cols-2 gap-5">
              {/* Witness Circle */}
              <Card 
                className="bento-card cursor-pointer card-hover bg-gradient-to-br from-white to-teal-50/50"
                onClick={() => navigate("/witness-circle")}
                data-testid="witness-circle-card"
              >
                <CardContent className="p-0">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center mb-4">
                    <Users className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">
                    Witness Circle
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Connect with an anonymous soul companion for mutual accountability
                  </p>
                  <span className="text-primary text-sm font-medium">Join →</span>
                </CardContent>
              </Card>

              {/* Referral card */}
              <Card className="bento-card bg-gradient-to-br from-white to-amber-50/50" data-testid="referral-card">
                <CardContent className="p-0">
                  <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
                    <Gift className="w-7 h-7 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">
                    Spread the Seeds
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Share your referral code and invite a friend into the movement.
                  </p>
                  {referralCode && (
                    <button
                      onClick={copyReferralCode}
                      className="flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2 text-sm font-mono font-semibold text-foreground hover:border-primary transition-colors"
                      data-testid="copy-referral-btn"
                    >
                      {referralCode}
                      {referralCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  )}
                </CardContent>
              </Card>

              {/* JVDH Intentional Insights */}
              <Card className="bento-card bg-gradient-to-br from-white to-teal-50/40" data-testid="gumroad-store-card">
                <CardContent className="p-0">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400/20 to-teal-500/20 flex items-center justify-center mb-4">
                    <Sparkles className="w-7 h-7 text-amber-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">
                    JVDH Intentional Insights
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Books, guides and resources from JVDH Intentional Insights to deepen your journey.
                  </p>
                  <a
                    href="https://intentionalinsights.gumroad.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary text-sm font-medium hover:text-primary/80"
                    data-testid="gumroad-store-link"
                  >
                    Browse the Store <ExternalLink className="w-4 h-4" />
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Crisis Support Box */}
            <Card className="rounded-3xl border border-rose-200/60 bg-gradient-to-r from-rose-50/60 to-pink-50/40" data-testid="crisis-support-box">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-rose-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-semibold text-foreground mb-1">
                      Need Immediate Support?
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      If you're experiencing a crisis or need someone to talk to, these services are available 24/7.
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/70 rounded-2xl p-4">
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 font-body">
                      <span>🇦🇺</span> Australia
                    </h4>
                    <div className="space-y-2">
                      <a href="tel:131114" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">Lifeline</span>
                        <span className="text-rose-600 font-bold">13 11 14</span>
                      </a>
                      <a href="tel:1300224636" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">Beyond Blue</span>
                        <span className="text-rose-600 font-bold">1300 22 4636</span>
                      </a>
                      <a href="tel:1800551800" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">Kids Helpline</span>
                        <span className="text-rose-600 font-bold">1800 55 1800</span>
                      </a>
                      <a href="tel:1800650890" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">SANE Australia</span>
                        <span className="text-rose-600 font-bold">1800 18 7263</span>
                      </a>
                    </div>
                  </div>
                  
                  <div className="bg-white/70 rounded-2xl p-4">
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 font-body">
                      <span>🇳🇿</span> New Zealand
                    </h4>
                    <div className="space-y-2">
                      <a href="tel:0800543354" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">Lifeline NZ</span>
                        <span className="text-rose-600 font-bold">0800 543 354</span>
                      </a>
                      <a href="tel:1737" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">Need to Talk?</span>
                        <span className="text-rose-600 font-bold">1737</span>
                      </a>
                      <a href="tel:0800376633" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">Depression Helpline</span>
                        <span className="text-rose-600 font-bold">0800 111 757</span>
                      </a>
                      <a href="tel:0800942890" className="flex items-center justify-between text-sm hover:bg-rose-50 p-2 rounded-lg transition-colors">
                        <span className="text-foreground font-medium">Youthline</span>
                        <span className="text-rose-600 font-bold">0800 376 633</span>
                      </a>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-center text-muted-foreground mt-4">
                  💜 You are not alone. Reaching out is a sign of strength.
                </p>
              </CardContent>
            </Card>

            {/* Privacy Badge */}
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 px-4 py-2 rounded-full">
                <Lock className="w-3 h-3 text-green-500" />
                <span>Your data is encrypted and private</span>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default DashboardPage;
